import { Box, Button, Center, HStack, Heading, Image, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeProvider';
import { Text } from '../../../components/atoms/Text';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FaBell } from 'react-icons/fa';
import { INotificationItem } from '@/service/club/types';
import { useMarkNotificationAsRead } from '@/service/club/hooks';
import { useOpenBook } from '@/pages/Bookstore/contexts/BookContext';
import { getEditorialIdentifier } from '@/pages/Bookstore/utils/formatters';

interface CardNotificationProps {
  notification: INotificationItem;
  onHandleClick?: () => void;
}

/** O backend anexa o log ao final do corpo do push (ex.: "... log: a1b2c3d4."). */
const BODY_LOG_REGEX = /\s*log:\s*(\S+?)\.?\s*$/i;

function normalizeLog(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

interface LogLineProps {
  label: string;
  value: string;
  muted: boolean;
}

function LogLine({ label, value, muted }: LogLineProps) {
  return (
    <Text width="100%" fontSize={'10px'} fontWeight={400} color={muted ? '#C4C4C4' : '#A8A8A8'} noOfLines={1}>
      {label}:{' '}
      <Text as="span" fontFamily={'mono'} fontWeight={500} color={muted ? '#BDBDBD' : '#9A9A9A'}>
        {value}
      </Text>
    </Text>
  );
}

export function CardNotification({ notification, onHandleClick }: CardNotificationProps) {
  const { colors } = useTheme();
  const { openBook } = useOpenBook();
  const { mutate: markAsRead } = useMarkNotificationAsRead();

  const [isRead, setIsRead] = useState(notification.isRead);

  const { id, hashCode, createdAt, pushSubject, pushBody, receivedBook } = notification;

  const formattedDate = format(new Date(createdAt), "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
  const cover = receivedBook?.book.coverOptimized || receivedBook?.book.cover;
  const editorial = receivedBook
    ? getEditorialIdentifier(
        receivedBook.book,
        (receivedBook as { monthReference?: number }).monthReference,
        (receivedBook as { yearReference?: number }).yearReference
      )
    : null;

  const rawSector = receivedBook?.book.sector;
  const sector = typeof rawSector === 'object' ? rawSector?.name : undefined;
  const bodyLog = normalizeLog(pushBody.match(BODY_LOG_REGEX)?.[1]);
  const cleanedBody = pushBody.replace(BODY_LOG_REGEX, '').trim();
  const notificationLog = normalizeLog(hashCode) || bodyLog;
  const deliveryLog = normalizeLog(receivedBook?.hashCode);
  const hasLogs = !!notificationLog || !!deliveryLog;

  const handleMarkAsRead = () => {
    if (isRead) return;
    setIsRead(true);
    markAsRead(id);
  };

  const handleCardClick = () => {
    handleMarkAsRead();
    if (receivedBook)
      openBook(
        receivedBook.book,
        true,
        undefined,
        receivedBook.hashCode,
        (receivedBook as { monthReference?: number }).monthReference,
        (receivedBook as { yearReference?: number }).yearReference
      );
  };

  const primary = colors.primaryColor;
  const cardBg = isRead ? '#FFFFFF' : `${primary}0A`;
  const cardBorder = isRead ? '1px solid #EEEEEE' : `1px solid ${primary}26`;
  const cardShadow = isRead ? '0px 1px 3px 0px rgba(0,0,0,0.04)' : '0px 8px 22px 0px rgba(0,0,0,0.10)';
  const titleColor = isRead ? '#9E9E9E' : '#1A1A1A';
  const bodyColor = isRead ? '#B5B5B5' : '#5F5F5F';

  return (
    <HStack
      position={'relative'}
      alignItems={'flex-start'}
      spacing={3}
      boxShadow={cardShadow}
      padding={'16px'}
      paddingLeft={isRead ? '16px' : '20px'}
      width={'100%'}
      borderRadius={'20px'}
      border={cardBorder}
      backgroundColor={cardBg}
      cursor={'pointer'}
      onClick={handleCardClick}
      transition="transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease"
      _active={{ transform: 'scale(0.98)' }}
    >
      {!isRead && (
        <Box
          position={'absolute'}
          left={0}
          top={0}
          bottom={0}
          width={'4px'}
          borderTopLeftRadius={'20px'}
          borderBottomLeftRadius={'20px'}
          backgroundColor={primary}
        />
      )}
      {receivedBook ? (
        <Box position={'relative'} flexShrink={0} width={'64px'} opacity={isRead ? 0.6 : 1}>
          <Image
            src={cover}
            alt={receivedBook.book.title}
            width={'100%'}
            aspectRatio={0.7}
            objectFit="cover"
            borderRadius={'6px'}
            border={'1px solid #ededed'}
            boxShadow={'rgba(100, 100, 111, 0.2) 0px 5px 8px 0px'}
            backgroundColor="white"
          />
        </Box>
      ) : (
        <Center
          flexShrink={0}
          width={'48px'}
          height={'48px'}
          borderRadius={'full'}
          backgroundColor={`${primary}1A`}
          opacity={isRead ? 0.6 : 1}
        >
          <FaBell size={20} color={primary} />
        </Center>
      )}
      <VStack width="100%" align={'flex-start'} spacing={1} flex={1} minWidth={0}>
        <HStack width="100%" justifyContent={'space-between'} alignItems={'flex-start'} spacing={2}>
          <Heading
            noOfLines={2}
            flex={'1'}
            minWidth={0}
            fontSize={'15px'}
            lineHeight={'1.3'}
            fontWeight={isRead ? 500 : 700}
            color={titleColor}
          >
            {pushSubject}
          </Heading>
          <Text flexShrink={0} fontSize="11px" fontWeight={isRead ? 400 : 600} color={isRead ? '#B5B5B5' : primary}>
            {formattedDate}
          </Text>
        </HStack>
        {!!cleanedBody && (
          <Text
            width="100%"
            textAlign={'start'}
            color={bodyColor}
            fontSize={12}
            fontWeight={400}
            noOfLines={receivedBook ? 2 : undefined}
            whiteSpace="pre-line"
            overflowWrap="break-word"
            wordBreak="break-word"
          >
            {cleanedBody}
          </Text>
        )}
        {receivedBook && (!!receivedBook.book.author || !!editorial || !!sector) && (
          <Text width="100%" noOfLines={1} fontSize={11} fontWeight={400} color="#9A9A9A">
            {[
              receivedBook.book.author && `Autor: ${receivedBook.book.author}`,
              editorial && `${editorial.label}: ${editorial.value}`,
              sector,
            ]
              .filter(Boolean)
              .join('  ·  ')}
          </Text>
        )}
        {hasLogs && (
          <VStack width="100%" spacing={'1px'} align={'stretch'} marginTop={'4px'}>
            {!!notificationLog && <LogLine label="Log de entrega" value={notificationLog} muted={isRead} />}
            {!!deliveryLog && <LogLine label="Log de venda" value={deliveryLog} muted={isRead} />}
          </VStack>
        )}
        {onHandleClick && (
          <Button
            marginLeft={'auto'}
            size={'xs'}
            variant={'unset'}
            color={primary}
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsRead();
              onHandleClick();
            }}
          >
            Ver desconto
          </Button>
        )}
      </VStack>
    </HStack>
  );
}
